import React from 'react';
import { JsonLd } from 'react-schemaorg';
import type { FAQPage } from 'schema-dts';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQPageSEOProps {
  faqs: FAQItem[];
}

/**
 * FAQPageSEO component renders FAQ structured data for Google Rich Results.
 *
 * Usage:
 * ```tsx
 * <FAQPageSEO
 *   faqs={[
 *     { question: "Was ist Webmetic?", answer: "Webmetic ist eine B2B Lead-Intelligence Plattform..." },
 *     { question: "Wie funktioniert die Preisgestaltung?", answer: "Starter ab 69 € im Monat..." },
 *   ]}
 * />
 * ```
 *
 * This will show FAQ snippets directly in Google search results.
 */
export const FAQPageSEO: React.FC<FAQPageSEOProps> = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;

  return (
    <JsonLd<FAQPage>
      item={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      }}
    />
  );
};

export default FAQPageSEO;
